import { bool, list, money, num, type CalcValues, type EstimateResult, type ServiceLine } from './types';

/**
 * Infor and Workday managed services share one model (Cendien service model):
 *   • minimum 10 hours per week, billed per 4-week period
 *   • three tiers; the hourly rate drops as the committed hours rise
 *     (Essential $175, Standard $170, Advanced $165)
 *   • 3-year term with two optional years; project work can run alongside
 */
export const ERP_TIERS = [
  { id: 'essential', name: 'Essential', minHours: 10, rate: 175 },
  { id: 'standard', name: 'Standard', minHours: 20, rate: 170 },
  { id: 'advanced', name: 'Advanced', minHours: 40, rate: 165 },
] as const;

export const PERIOD_WEEKS = 4;
export const PERIODS_PER_YEAR = 13;

export function erpTier(hoursPerWeek: number) {
  return [...ERP_TIERS].reverse().find((t) => hoursPerWeek >= t.minHours) ?? ERP_TIERS[0];
}

function erpEstimate(v: CalcValues, product: string): EstimateResult {
  const hours = Math.max(10, num(v.hours, 10));
  const tier = erpTier(hours);
  const periodHours = hours * PERIOD_WEEKS;
  const support = periodHours * tier.rate;
  const onsiteDays = num(v.onsiteDays, 0);
  const projectHours = num(v.projectHours, 0);
  const onsite = (onsiteDays * 8 * tier.rate) / PERIODS_PER_YEAR;
  const project = (projectHours * tier.rate) / PERIODS_PER_YEAR;
  const surge = bool(v.surge) ? (60 * tier.rate) / PERIODS_PER_YEAR : 0;
  const mid = support + onsite + project + surge;
  const modules = list(v.modules);
  const breakdown = [
    { label: `${tier.name} support (${periodHours} hours)`, amount: support },
    ...(project ? [{ label: 'Project and enhancement hours', amount: project }] : []),
    ...(onsite ? [{ label: 'On-site days', amount: onsite }] : []),
    ...(surge ? [{ label: 'Year-end and open-enrollment surge hours', amount: surge }] : []),
  ];
  return {
    low: mid,
    high: mid,
    mid,
    unitLabel: 'per 4-week period',
    breakdown,
    facts: [
      `${tier.name} tier at ${money(tier.rate)} per hour, ${hours} hours a week committed`,
      `About ${money(mid * PERIODS_PER_YEAR)} a year (13 four-week periods)`,
    ],
    summary: `${product} managed services: ${hours} hours/week (${tier.name}, ${money(tier.rate)}/hr), ${onsiteDays} on-site days/yr, ${projectHours} project hours/yr${bool(v.surge) ? ', surge hours' : ''}. Modules: ${modules.join(', ') || 'not specified'}. Estimate: ${money(mid)} per 4-week period (${money(mid * PERIODS_PER_YEAR)} per year).`,
    note: hours < 20 ? `Moving to 20 hours a week lowers the rate to ${money(ERP_TIERS[1].rate)} per hour.` : hours < 40 ? `Moving to 40 hours a week lowers the rate to ${money(ERP_TIERS[2].rate)} per hour.` : undefined,
  };
}

interface ErpOpts {
  id: 'infor' | 'workday';
  product: string;
  parentPath: string;
  pricingPath: string;
  landingPrefix: string;
  modules: string[];
  releaseNote: string;
  credential: string;
  catalogExtra: { id: string; name: string; desc: string; price: string; unit: string }[];
  faqs: { q: string; a: string }[];
  explainerVideoKey?: ServiceLine['explainerVideoKey'];
}

export function makeErpLine(o: ErpOpts): ServiceLine {
  const P = o.product;
  return {
    id: o.id,
    name: `${P} managed services`,
    short: P,
    formService: `${P} managed services`,
    parentPath: o.parentPath,
    parentLabel: `${P} support`,
    pricingPath: o.pricingPath,
    landingPrefix: o.landingPrefix,
    pricingTitle: `${P} Managed Services Pricing Calculator`,
    pricingIntro: `Estimate the cost of ${P} managed services in under a minute. Pick your weekly hours and see your price per 4-week period. The more hours you commit, the lower the hourly rate.`,
    heroBullets: [
      'Hourly rates from $165 to $175, by tier',
      'Minimum of 10 hours a week, billed every 4 weeks',
      o.credential,
      'Approve every consultant assigned to you',
    ],
    calcIntro: 'Sample values for a mid-size organization are filled in. Change them to match yours.',
    inputs: [
      { kind: 'slider', id: 'hours', label: 'Support hours per week', min: 10, max: 80, step: 5, inputMax: 160, suffix: 'hrs',
        hint: '10 hours is the minimum. Committing 20 or 40 hours moves you to a lower hourly rate.' },
      { kind: 'chips', id: 'modules', label: `${P} modules in scope`, options: o.modules },
      { kind: 'slider', id: 'projectHours', label: 'Project and enhancement hours per year', min: 0, max: 1000, step: 20, inputMax: 4000,
        hint: 'Reports, integrations, configuration changes and upgrades, at your tier rate.' },
      { kind: 'slider', id: 'onsiteDays', label: 'On-site days per year', min: 0, max: 24, step: 1, inputMax: 100,
        hint: 'Most support is remote. Add days for go-lives, workshops or year-end.' },
      { kind: 'toggle', id: 'surge', label: 'Year-end and open-enrollment surge hours',
        hint: 'Adds 60 hours a year for payroll year-end, W-2, ACA and benefits enrollment windows.' },
    ],
    defaults: { hours: 20, modules: o.modules.slice(0, 2), projectHours: 200, onsiteDays: 4, surge: false },
    estimate: (v) => erpEstimate(v, P),
    plansTitle: `${P} managed services packages`,
    plansIntro: `Every package includes a named consultant you approve, ticket intake through your own queue, a weekly meeting with your Cendien project manager, quarterly leadership reviews and monthly reporting. ${o.releaseNote}`,
    plans: [
      {
        id: 'essential', name: 'Essential', blurb: '10–19 hours a week', price: '$175', unit: 'per hour · from $7,000 per 4 weeks', icon: 'erp-essential',
        bestFor: `Stable ${P} environments that need dependable coverage for tickets, patches and small changes.`,
        items: ['Named functional or technical consultant', 'Tickets through your own queue', 'Weekly check-in with your project manager', 'Patch and release review', 'Monthly service report'],
        calcPreset: { hours: 10 }, schemaPrice: { min: 175, max: 175, unitText: 'per hour, 10 hours per week minimum' },
      },
      {
        id: 'standard', name: 'Standard', blurb: '20–39 hours a week', price: '$170', unit: 'per hour · from $13,600 per 4 weeks', icon: 'erp-standard', feature: true,
        bestFor: `Organizations running payroll, HR and finance on ${P} that need functional and technical depth every week.`,
        items: ['Everything in Essential', 'Functional and technical coverage', 'Release and update testing', 'Integration and interface monitoring', 'Quarterly leadership review and roadmap'],
        calcPreset: { hours: 20 }, schemaPrice: { min: 170, max: 170, unitText: 'per hour, 20 hours per week minimum' },
      },
      {
        id: 'advanced', name: 'Advanced', blurb: '40+ hours a week', price: '$165', unit: 'per hour · from $26,400 per 4 weeks', icon: 'erp-advanced',
        bestFor: `Large or complex ${P} environments that want a dedicated team in place of in-house ERP staff.`,
        items: ['Everything in Standard', 'Dedicated multi-person team', 'Coverage for payroll and year-end critical windows', 'Report and integration development included', 'Optimization and AI roadmap'],
        calcPreset: { hours: 40 }, schemaPrice: { min: 165, max: 165, unitText: 'per hour, 40 hours per week minimum' },
      },
    ],
    catalogTitle: `One-time ${P} projects and add-ons`,
    catalog: [
      {
        id: 'projects', group: 'Projects', intro: 'Fixed-price or estimated projects, run alongside your managed services.',
        items: [
          { id: 'healthcheck', name: `${P} health check`, desc: 'Configuration, security roles, integrations and open issues reviewed, with a prioritized fix list.', price: 'From $9,500', unit: 'fixed' },
          { id: 'aiassess', name: `${P} AI integration assessment`, desc: `Options for AI inside ${P}, including Cendien's MCP connectors, with a costed roadmap.`, price: '$15,000', unit: 'fixed, 2–3 weeks' },
          ...o.catalogExtra,
        ],
      },
      {
        id: 'staffing', group: 'Extra hands', intro: 'Short-term help without changing your package.',
        items: [
          { id: 'ondemand', name: 'On-demand hours', desc: 'Extra hours beyond your commitment, billed at your tier rate.', price: 'Your tier rate', unit: 'per hour' },
          { id: 'onsite', name: 'On-site day', desc: 'A consultant at your site for workshops, go-lives or year-end.', price: '8 hours', unit: 'at your tier rate, plus travel' },
          { id: 'training', name: 'End-user and admin training', desc: 'Role-based sessions with recorded walkthroughs and quick-reference guides.', price: 'Quoted', unit: 'per session' },
        ],
      },
    ],
    answerTitle: `How much do ${P} managed services cost?`,
    answer: `Cendien prices ${P} managed services by the hour with a weekly commitment: $175 per hour for 10 to 19 hours a week, $170 for 20 to 39 hours, and $165 for 40 hours or more, billed every four weeks. A 20-hour-a-week Standard package is ${money(20 * 4 * 170)} per 4-week period, about ${money(20 * 4 * 170 * 13)} a year. Terms run three years with two optional years, and project work can run alongside at the same rate.`,
    answerBullets: ['Lower hourly rate as committed hours rise', 'Billed every 4 weeks, no annual lump sum', 'Consultants you interview and approve', 'Project work at the same rate'],
    drivers: [
      { icon: 'erp-hours', title: 'Weekly hours', body: 'The commitment sets your tier and your rate. Most organizations start at 20 hours and adjust after the first quarter.' },
      { icon: 'erp-modules', title: 'Modules and integrations', body: 'Payroll, benefits, time, finance and supply chain each add workload, as do integrations to banks, carriers and state agencies.' },
      { icon: 'erp-calendar', title: 'Critical calendar windows', body: 'Payroll year-end, W-2, ACA filing and open enrollment need extra hours on fixed dates.' },
      { icon: 'erp-roadmap', title: 'Project work', body: 'Reports, integrations, upgrades and AI features are priced at your tier rate, so you never pay a project premium.' },
    ],
    faqs: o.faqs,
    explainerVideoKey: o.explainerVideoKey,
    related: [],
    barRef: 30000,
    examples: (b) => [
      { label: 'Essential · 10 hours a week', values: { ...b, hours: 10 } },
      { label: 'Standard · 20 hours a week', values: { ...b, hours: 20 } },
      { label: 'Advanced · 40 hours a week', values: { ...b, hours: 40 } },
    ],
    compare: {
      title: `${P} support options compared`,
      columns: ['', `Cendien ${P} managed services`, `Hiring an in-house ${P} analyst`, 'On-demand consultants'],
      rows: [
        ['Typical cost', 'From $7,000 per 4 weeks', '$95,000–$140,000 a year plus benefits', '$175–$250 an hour, billed as used'],
        ['Coverage', 'Functional and technical team', 'One person’s skills', 'Whoever is available'],
        ['Vacations and turnover', 'Covered by the team', 'Gaps until you rehire', 'No continuity'],
        ['Knows your configuration', 'Yes, documented runbooks', 'Yes, until they leave', 'Relearned each time'],
        ['Budget predictability', 'Fixed weekly commitment', 'Salary plus hiring costs', 'Varies month to month'],
      ],
      note: 'In-house and on-demand figures are typical market ranges, shown for planning only.',
    },
  };
}
